'use client';

import { useEffect, useContext, createContext } from 'react';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider as Provider } from '@mui/x-date-pickers/LocalizationProvider';

import { useSettingsContext } from 'src/components/settings';

import en from './en.json';
import ar from './ar.json';
import { useRouter, usePathname } from 'next/navigation';

// ----------------------------------------------------------------------

const LocaleContext = createContext();

const translations = { en, ar };

export function useLocale() {
  return useContext(LocaleContext);
}

export function LocalizationProvider({ children }) {
  const settings = useSettingsContext();
  const { locale, direction } = settings.state;
  const router = useRouter();
  const pathname = usePathname();
  const setLocale = (newLocale) => {
    // Extract the path after the current locale
    const segments = pathname.split('/');
    segments[1] = newLocale; // Replace locale part with the new one

    const newPath = segments.join('/');
    router.push(newPath);

    settings.setState({
      ...settings.state,
      locale: newLocale,
      direction: newLocale === 'ar' ? 'rtl' : 'ltr',
    });
  };

  const setDirection = (newDirection) => {
    settings.setState({
      ...settings.state,
      direction: newDirection,
    });
  };

  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = locale;
  }, [direction, locale]);

  const value = {
    locale,
    setLocale,
    direction,
    setDirection,
    t: (key) => translations[locale][key] || key,
    isRTL: direction === 'rtl',
  };

  return (
    <LocaleContext.Provider value={value}>
      <Provider dateAdapter={AdapterDayjs}>{children}</Provider>
    </LocaleContext.Provider>
  );
}
